/**
 * Composable per gestione paginazione standardizzata
 *
 * @description
 * Fornisce funzionalità di paginazione riutilizzabili per tutti i widget e componenti.
 * Gestisce automaticamente i cambiamenti di pagina e dimensione pagina con callback
 * per il ricaricamento dati.
 */

import { ref } from 'vue';
import { createPaginationData } from '@/mixins/filterHandler/lib';
import type { IPaginationData } from '@/mixins/filterHandler/lib';

/**
 * Opzioni di configurazione per usePagination
 */
export interface PaginationOptions {
	/** Numero iniziale di elementi per pagina (default: 25) */
	initialPerPage?: number;
	/** Pagina iniziale (default: 1) */
	initialPage?: number;
	/** Callback chiamato quando i dati devono essere ricaricati */
	onDataChange?: () => void | Promise<void>;
}

/**
 * Composable per gestione paginazione
 *
 * @param options - Opzioni di configurazione
 * @returns Oggetto con stato paginazione e handlers
 *
 * @example
 * // Uso base in un widget
 * const {
 *   dataPagination,
 *   onPaginationPerPageChange,
 *   onPaginationPageChange
 * } = usePagination({
 *   initialPerPage: 5,
 *   onDataChange: getData
 * });
 *
 * @example
 * // Nel template
 * <Pagination
 *   :total="searchResultTotal"
 *   :page-size="dataPagination.perPage"
 *   :current-page="dataPagination.page"
 *   @size-change="onPaginationPerPageChange"
 *   @current-change="onPaginationPageChange"
 * />
 */
export function usePagination(options: PaginationOptions = {}) {
	const { initialPerPage = 25, initialPage = 1, onDataChange } = options;

	// Stato paginazione tipizzato
	const dataPagination = ref<IPaginationData>(createPaginationData(initialPerPage, initialPage));

	/**
	 * Gestisce il cambio del numero di elementi per pagina
	 *
	 * @param val - Nuovo numero di elementi per pagina
	 */
	function onPaginationPerPageChange(val: number): void {
		dataPagination.value.perPage = val;
		dataPagination.value.page = 1; // Reset alla prima pagina quando cambia la dimensione
		if (onDataChange) {
			onDataChange();
		}
	}

	/**
	 * Gestisce il cambio della pagina corrente
	 *
	 * @param val - Nuovo numero di pagina
	 */
	function onPaginationPageChange(val: number): void {
		dataPagination.value.page = val;
		if (onDataChange) {
			onDataChange();
		}
	}

	/**
	 * Resetta la paginazione alla prima pagina
	 * Utile dopo filtri o ricerche
	 */
	function resetToFirstPage(): void {
		dataPagination.value.page = 1;
	}

	/**
	 * Imposta una nuova dimensione pagina mantenendo la logica di reset
	 *
	 * @param newPerPage - Nuova dimensione pagina
	 */
	function setPerPage(newPerPage: number): void {
		dataPagination.value.perPage = newPerPage;
		dataPagination.value.page = 1;
		if (onDataChange) {
			onDataChange();
		}
	}

	return {
		/** Oggetto paginazione reattivo */
		dataPagination,
		/** Handler per cambio elementi per pagina */
		onPaginationPerPageChange,
		/** Handler per cambio pagina corrente */
		onPaginationPageChange,
		/** Funzione per reset alla prima pagina */
		resetToFirstPage,
		/** Funzione per impostare nuova dimensione pagina */
		setPerPage,
	};
}

/**
 * Tipo di ritorno del composable usePagination
 * Utile per type hints e documentazione
 */
export type PaginationComposable = ReturnType<typeof usePagination>;
